import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { areEqual } from 'Utils/equalityChecks';
import { Api } from 'Utils/api';

import { Option, CustomFieldConfig } from '../types';

import classes from './leadsWithoutReferrals.module.css';

interface ContactWithoutReferral {
  contactId: string;
  contactName: string;
  companyName: string | null;
  email: string | null;
  phone: string | null;
  type: string;
  createdAt: string;
}

interface LeadsWithoutReferralsProps {
  locationId: string;
  ssoToken: string;
  referralPartnerOptions: Option[];
  customFieldConfig: CustomFieldConfig;
  isLoadingContacts: boolean;
}

function LeadsWithoutReferralsComponent({
  locationId,
  ssoToken,
  referralPartnerOptions,
  // customFieldConfig not needed - we update contact.referred_by_id directly
  isLoadingContacts,
}: LeadsWithoutReferralsProps) {
  const [contacts, setContacts] = useState<ContactWithoutReferral[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [assignSearchTerm, setAssignSearchTerm] = useState('');
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());

  const loadContactsWithoutReferrals = useCallback(async () => {
    if (!locationId || !ssoToken) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Fetch contacts without referrals from the backend
      const response = await Api.get('/ghl/embed/contacts', {
        params: {
          locationId,
          without_referral: true,
          type: 'lead',
          limit: 500,
        },
        headers: { 'X-GHL-SSO': ssoToken },
      });

      // Handle response format
      const contactsData = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
          ? response.data
          : [];

      // Map to display format
      const mappedContacts: ContactWithoutReferral[] = contactsData.map((contact: any) => {
        const firstName = contact.first_name || contact.firstName || '';
        const lastName = contact.last_name || contact.lastName || '';
        const contactName = [firstName, lastName].filter((n) => n.trim()).join(' ').trim()
          || contact.display_name
          || contact.displayName
          || '(No Name)';

        return {
          contactId: contact.id,
          contactName,
          companyName: contact.company_name || contact.companyName || null,
          email: contact.email || null,
          phone: contact.phone || null,
          type: contact.type || 'lead',
          createdAt: contact.date_added || contact.dateAdded || contact.created_at || '',
        };
      });

      // Sort by created date descending
      mappedContacts.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      setContacts(mappedContacts);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Error loading contacts without referrals:', err);
      setError('Failed to load contacts. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [locationId, ssoToken]);

  useEffect(() => {
    loadContactsWithoutReferrals();
  }, [loadContactsWithoutReferrals]);

  // Handle assigning a referral partner
  const handleAssignReferralPartner = useCallback(async (contactId: string, partner: Option) => {
    setSavingIds((prev) => new Set(prev).add(contactId));
    setAssigningId(null);
    setAssignSearchTerm('');

    try {
      // Update the contact's referred_by_id directly
      await Api.put(`/ghl/embed/contacts/${contactId}`, {
        referred_by_id: partner.id,
      }, {
        headers: { 'X-GHL-SSO': ssoToken },
      });

      // Also update any opportunities for this contact
      try {
        const oppResponse = await Api.get('/ghl/embed/opportunities', {
          params: {
            'filter[contact_id]': contactId,
            limit: 100,
          },
          headers: { 'X-GHL-SSO': ssoToken },
        });

        const opportunities = Array.isArray(oppResponse?.data?.data)
          ? oppResponse.data.data
          : [];

        // Update each opportunity with the new referral partner
        await Promise.all(
          opportunities.map((opp: any) =>
            Api.put(`/ghl/embed/opportunities/${opp.id}`, {
              referral_partner_id: partner.id,
            }, {
              headers: { 'X-GHL-SSO': ssoToken },
            })
          )
        );
      } catch (oppErr) {
        // Log but don't fail the whole operation if opportunity update fails
        // eslint-disable-next-line no-console
        console.error('Failed to update opportunities:', oppErr);
      }

      // Remove from list since it now has a referral
      setContacts((prevContacts) => prevContacts.filter((c) => c.contactId !== contactId));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to assign referral partner:', err);
      setError('Failed to assign referral partner. Please try again.');
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(contactId);
        return next;
      });
    }
  }, [ssoToken]);

  // Filter referral partner options for dropdown
  const filteredAssignOptions = useMemo(() => {
    if (!assignSearchTerm) return referralPartnerOptions.slice(0, 10);
    const searchLower = assignSearchTerm.toLowerCase();
    return referralPartnerOptions
      .filter(
        (option) =>
          option.label.toLowerCase().includes(searchLower)
          || (option.companyName && option.companyName.toLowerCase().includes(searchLower))
      )
      .slice(0, 10);
  }, [referralPartnerOptions, assignSearchTerm]);

  const filteredContacts = useMemo(() => {
    if (!searchTerm) return contacts;
    const searchLower = searchTerm.toLowerCase();
    return contacts.filter(
      (contact) =>
        contact.contactName.toLowerCase().includes(searchLower)
        || (contact.companyName && contact.companyName.toLowerCase().includes(searchLower))
        || (contact.email && contact.email.toLowerCase().includes(searchLower))
        || (contact.phone && contact.phone.includes(searchTerm))
    );
  }, [contacts, searchTerm]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return '-';
    }
  };

  const handleAssignClick = (contactId: string) => {
    setAssigningId(contactId);
    setAssignSearchTerm('');
  };

  const handleCancelAssign = () => {
    setAssigningId(null);
    setAssignSearchTerm('');
  };

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading contacts without referrals...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger alert-dismissible" role="alert">
        {error}
        <button type="button" className="btn-close" onClick={() => setError(null)} aria-label="Close" />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <h5 className="mb-3">Contacts Without Referral Partners</h5>

      <div className="mb-3">
        <p className="text-muted mb-2">
          Total contacts without referral partner: <strong>{contacts.length}</strong>
        </p>
        <input
          type="text"
          className="form-control"
          placeholder="Search by name, email, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: '400px' }}
        />
      </div>

      {filteredContacts.length === 0 ? (
        <div className="alert alert-info">
          {searchTerm
            ? 'No contacts found matching your search.'
            : 'All contacts have referral partners assigned!'}
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead className="table-light">
              <tr>
                <th>Contact Name</th>
                <th style={{ minWidth: '200px' }}>Assign Referral</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Created Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredContacts.map((contact) => (
                <tr key={contact.contactId}>
                  <td>{contact.contactName}</td>
                  <td>
                    {assigningId === contact.contactId ? (
                      <div className={classes.assignDropdown}>
                        <input
                          type="text"
                          className="form-control form-control-sm mb-1"
                          placeholder="Search referral partners..."
                          value={assignSearchTerm}
                          onChange={(e) => setAssignSearchTerm(e.target.value)}
                          autoFocus
                        />
                        {isLoadingContacts ? (
                          <div className="text-muted small">Loading...</div>
                        ) : (
                          <ul className={classes.dropdownList}>
                            {filteredAssignOptions.map((option) => (
                              <li key={option.id}>
                                <button
                                  type="button"
                                  className={classes.dropdownItem}
                                  onClick={() => handleAssignReferralPartner(contact.contactId, option)}
                                >
                                  {option.label}
                                  {option.companyName && (
                                    <small className="text-muted"> - {option.companyName}</small>
                                  )}
                                </button>
                              </li>
                            ))}
                            {filteredAssignOptions.length === 0 && (
                              <li className="text-muted small p-2">No matches found</li>
                            )}
                          </ul>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-secondary p-0 mt-1"
                          onClick={handleCancelAssign}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div>
                        {savingIds.has(contact.contactId) ? (
                          <span className="text-muted">
                            <span className="spinner-border spinner-border-sm me-1" role="status" />
                            Assigning...
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleAssignClick(contact.contactId)}
                          >
                            + Assign Referral
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td>{contact.email || '-'}</td>
                  <td>{contact.phone || '-'}</td>
                  <td>{formatDate(contact.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const LeadsWithoutReferralsMemo = memo(LeadsWithoutReferralsComponent, areEqual);

export { LeadsWithoutReferralsMemo as LeadsWithoutReferrals };
